/**
 * The detail of the entity or relationship selected in the drawing or in the list: how it is
 * built and its settings, all in view. Pure: the recipe in, edits out.
 */

import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  type ColumnProfile,
  type EntityDescriptor,
  effectiveGrain,
  type Preview,
  type Proposal,
  type RelationshipPreview,
  type RelationshipRule,
  type RowExplanation,
  type RowsQuery,
  resolveEndpoint,
  type SubjectEntity,
} from '@/services/api/structuredIngest'
import {
  ColumnPropertyTable,
  entityPropertyMappings,
  propertySlug,
  relationshipPropertyMappings,
} from './ColumnPropertyTable'
import { isSourceRow, verifiedIdentityProperty } from './identity'
import {
  DescriptorCard,
  FactCard,
  SectionTitle,
  SubjectEntityCard,
  SummaryCard,
} from './recipe-cards'
import { editByName } from './recipe-edits'
import { grainChip, parseGrain } from './recipe-graph-data'
import { textExtractionColumns, withTextExtraction } from './text-extraction'

interface InspectorData {
  recipe: Proposal
  preview: Preview
  columns: ColumnProfile[]
  measureColumns: string[]
  rowsTotal: number
  schemaLabels: string[]
  selectedId: string | null
  /** Row-lens mode: the row under the lens (its cells are shown per entity). */
  row: RowExplanation | null
}

interface InspectorActions {
  onChange: (recipe: Proposal) => void
  onShowRows: (query: RowsQuery, subtitle: string) => void
}

function MappingRow({ what, from, to }: { what: string; from: string; to: string }) {
  return (
    <li className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-baseline gap-2 text-xs md:grid-cols-[9rem_1fr]">
      <span className="text-muted-foreground">{what}</span>
      <span className="flex flex-wrap items-center gap-1">
        <span className="font-mono max-md:min-w-0 max-md:break-words">{from}</span>
        {to && (
          <>
            <ArrowRight aria-hidden className="size-3 text-muted-foreground max-md:shrink-0" />
            <span className="font-mono text-primary max-md:min-w-0 max-md:break-words">{to}</span>
          </>
        )}
      </span>
    </li>
  )
}

function Cells({ cells }: { cells: Record<string, string> }) {
  return (
    <>
      {Object.entries(cells).map(([column, value]) => (
        <span key={column} className="mr-2 font-mono text-foreground">
          {column}={value || '∅'}
        </span>
      ))}
    </>
  )
}

function EntityInRow({ name, row }: { name: string; row: RowExplanation | null }) {
  const { t } = useTranslation()
  const lensEntity = row?.entities.find(e => e.name === name)
  if (!lensEntity) return null
  return (
    <p className="text-xs text-muted-foreground max-md:break-words">
      {t('tableMapping.inspector.inRow')}{' '}
      {lensEntity.enabled === false ? (
        <>
          {t('tableMapping.inspector.inRowOff')} <Cells cells={lensEntity.cells} />
        </>
      ) : lensEntity.missing ? (
        <span>{t('tableMapping.inspector.inRowNone')}</span>
      ) : (
        <>
          <Cells cells={lensEntity.cells} />→{' '}
          <span className="font-mono text-foreground">{lensEntity.key}</span>
        </>
      )}
    </p>
  )
}

/** The relationship property a grain is kept in: none for a pair, `category` for a column's values. */
function grainProperty(grain: string): string {
  const parsed = parseGrain(grain)
  if (parsed.kind === 'pair') return ''
  return parsed.kind === 'other' ? parsed.grain : parsed.kind
}

function dateColumnOf(recipe: Proposal, columns: ColumnProfile[]): string | undefined {
  const dateRoles = new Set(['date', 'timestamp'])
  return (recipe.columns ?? [])
    .filter(c => dateRoles.has(c.role))
    .map(c => ({ name: c.name, distinct: columns.find(p => p.name === c.name)?.n_distinct ?? 0 }))
    .filter(c => c.distinct > 1)
    .sort((a, b) => b.distinct - a.distinct)[0]?.name
}

function RelationshipInRow({ name, row }: { name: string; row: RowExplanation | null }) {
  const { t } = useTranslation()
  const lensRel = row?.relationships.find(r => r.name === name)
  if (!lensRel) return null
  return (
    <p className="text-xs text-muted-foreground max-md:break-words">
      {t('tableMapping.inspector.inRow')}{' '}
      {lensRel.in_scope ? (
        <>
          {lensRel.from_key} → {lensRel.to_key}
          {Object.keys(lensRel.grain).length > 0 && `, ${Object.values(lensRel.grain).join(', ')}`}
          {'. '}
          <span className="text-foreground">
            {t('tableMapping.inspector.oneOfRows', { count: lensRel.edge_rows })}
          </span>
        </>
      ) : (
        t('tableMapping.inspector.notPart')
      )}
    </p>
  )
}

function LinkMapping({
  rule,
  preview,
  recipe,
  columns,
}: {
  rule: RelationshipRule
  preview: RelationshipPreview | null
  recipe: Proposal
  columns: ColumnProfile[]
}) {
  const { t } = useTranslation()
  const grain = effectiveGrain(rule, preview)
  const dateColumn = dateColumnOf(recipe, columns)
  const structural = rule.kind === 'structural'
  const mappings = relationshipPropertyMappings(rule, recipe)
  return (
    <div className="flex flex-col gap-1.5">
      <SectionTitle help={t('tableMapping.inspector.rowsHelp')}>
        {t('tableMapping.inspector.rowsTitle')}
      </SectionTitle>
      <ul className="flex flex-col gap-1">
        <MappingRow
          what={t('tableMapping.inspector.between')}
          from={`${rule.from} → ${rule.to}`}
          to={rule.type.replace(/^new:/, '')}
        />
        {!structural && (
          <>
            <MappingRow
              what={t('tableMapping.inspector.onePer')}
              from={grainChip(grain, t)}
              to={grainProperty(grain)}
            />
            <MappingRow
              what={t('tableMapping.inspector.rowsCounted')}
              from={t('tableMapping.inspector.everyRow')}
              to="count"
            />
            {dateColumn && (
              <MappingRow
                what={t('tableMapping.inspector.datedBy')}
                from={dateColumn}
                to="first_ts, last_ts"
              />
            )}
            {(rule.measures ?? []).map(m => (
              <MappingRow
                key={m}
                what={t('tableMapping.inspector.totalOf')}
                from={m}
                to={`sum_${propertySlug(m)}`}
              />
            ))}
            {rule.direction_rule && (
              <MappingRow
                what={t('tableMapping.inspector.direction')}
                from={rule.direction_rule}
                to="IN / OUT"
              />
            )}
            {(rule.filters ?? []).map(f => (
              <MappingRow
                key={`${f.column}:${f.keep}`}
                what={t('tableMapping.inspector.onlyRowsWhere')}
                from={f.column}
                to={f.keep}
              />
            ))}
          </>
        )}
      </ul>
      {mappings.length > 0 && <ColumnPropertyTable mappings={mappings} target="relationship" />}
    </div>
  )
}

function DetailHeading({ name, type }: { name: string; type: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <h4 className="text-sm font-semibold">{name}</h4>
      <span className="font-mono text-xs text-muted-foreground">{type.replace(/^new:/, '')}</span>
    </div>
  )
}

/** Every detail takes the selected item, what the inspector knows, and its actions. */
interface DetailProps<T> {
  item: T
  data: InspectorData
  actions: InspectorActions
}

function SubjectDetail({
  item: subject,
  data: { recipe, schemaLabels },
  actions,
}: DetailProps<SubjectEntity>) {
  return (
    <SubjectEntityCard
      entity={subject}
      schemaLabels={schemaLabels}
      onChange={next =>
        actions.onChange({
          ...recipe,
          subject_entities: editByName(recipe.subject_entities, subject.name, () => next),
        })
      }
    />
  )
}

function DescriptorDetail({ item: descriptor, data, actions }: DetailProps<EntityDescriptor>) {
  const { recipe, preview, rowsTotal, schemaLabels, row } = data
  return (
    <div className="flex flex-col gap-3">
      <DetailHeading name={descriptor.name} type={descriptor.schema_type} />
      <DescriptorCard
        descriptor={descriptor}
        preview={preview.descriptors?.find(p => p.name === descriptor.name) ?? null}
        rowsTotal={rowsTotal}
        schemaLabels={schemaLabels}
        onChange={next =>
          actions.onChange({
            ...recipe,
            entity_descriptors: editByName(recipe.entity_descriptors, descriptor.name, () => next),
          })
        }
      />
      <ColumnPropertyTable
        mappings={entityPropertyMappings(descriptor, recipe)}
        identifier={isSourceRow(descriptor) ? null : verifiedIdentityProperty(descriptor)}
        extraction={{
          columns: textExtractionColumns(recipe).filter(
            column => column.entity === descriptor.name
          ),
          onToggle: (column, enabled) =>
            actions.onChange(withTextExtraction(recipe, column, enabled)),
        }}
      />
      <EntityInRow name={descriptor.name} row={row} />
    </div>
  )
}

function RelationshipDetail({ item: rule, data, actions }: DetailProps<RelationshipRule>) {
  const { recipe, preview, columns, measureColumns, row } = data
  const relPreview = preview.relationships?.find(p => p.name === rule.name) ?? null
  // Shown with the entities' real names (the model may write `subject_account` for
  // "0670427003/0800"); the stored rule keeps its own endpoint spelling.
  const shown: RelationshipRule = {
    ...rule,
    from: resolveEndpoint(rule.from, recipe) ?? rule.from,
    to: resolveEndpoint(rule.to, recipe) ?? rule.to,
  }
  const change = (next: RelationshipRule) =>
    actions.onChange({
      ...recipe,
      relationships: editByName(recipe.relationships, rule.name, () => ({
        ...next,
        from: rule.from,
        to: rule.to,
      })),
    })
  return (
    <div className="flex flex-col gap-3">
      <DetailHeading name={rule.name} type={rule.type} />
      {rule.kind === 'structural' ? (
        <FactCard rule={shown} preview={relPreview} onChange={change} />
      ) : (
        <SummaryCard
          rule={shown}
          preview={relPreview}
          measureColumns={measureColumns}
          onShowRows={actions.onShowRows}
          onChange={change}
        />
      )}
      <LinkMapping rule={shown} preview={relPreview} recipe={recipe} columns={columns} />
      <RelationshipInRow name={rule.name} row={row} />
    </div>
  )
}

/** The detail of one selected entity or relationship. Null when nothing matches. */
export function SelectionDetail({
  data,
  actions,
}: {
  data: InspectorData
  actions: InspectorActions
}) {
  const { recipe, selectedId } = data
  const subject = recipe.subject_entities?.find(s => s.name === selectedId)
  if (subject) return <SubjectDetail item={subject} data={data} actions={actions} />
  const descriptor = recipe.entity_descriptors?.find(d => d.name === selectedId)
  if (descriptor) return <DescriptorDetail item={descriptor} data={data} actions={actions} />
  const rule = recipe.relationships?.find(r => r.name === selectedId)
  if (rule) return <RelationshipDetail item={rule} data={data} actions={actions} />
  return null
}
